import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { PlusIcon } from 'lucide-react';
import type { DeskStage, TicketListItem } from '@erp/shared';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { deskDetailQuery, ticketsListQuery, useOperatedClientIds } from './tickets-queries';
import { useMoveTicketStage } from './tickets-mutations';
import { openDeskListParams } from './ticket-list-params';
import { initials } from './ticket-format';
import { CountPill, EmptyData, PriorityChip, SlaChip } from './ticket-chips';
import { MirroredBadge } from './mirrored-badge';
import { listSlaState } from './ticket-sla';

const TICKET_PREFIX = 'ticket-';
const STAGE_PREFIX = 'stage-';

export function TicketBoard({
  deskId,
  searchText,
  group,
  responsibleId,
  onNewTicket,
}: {
  deskId: number;
  searchText: string;
  /** v11: filtro por grupo de tickets (undefined = todos). */
  group?: number;
  /** spec 066: "Meus tickets" ligado = o id de quem está logado */
  responsibleId?: number;
  onNewTicket?: (stageId: number) => void;
}) {
  const deskQ = useQuery(deskDetailQuery(deskId));
  const listOptions = useMemo(
    () =>
      ticketsListQuery(
        openDeskListParams({ desk: deskId, q: searchText, group, responsibleId }, 200)
      ),
    [deskId, searchText, group, responsibleId]
  );
  const listQ = useQuery(listOptions);
  const move = useMoveTicketStage(listOptions.queryKey);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const operated = useOperatedClientIds();

  if (deskQ.isPending || listQ.isPending) return <BoardSkeleton />;
  if (deskQ.isError || listQ.isError) return <EmptyData message="Erro ao carregar o quadro" />;

  const stages = [...deskQ.data.stages].sort((a, b) => a.position - b.position);
  const items = listQ.data.items;

  const byStage = new Map<number, TicketListItem[]>();
  for (const t of items) {
    const arr = byStage.get(t.stage.id);
    if (arr) arr.push(t);
    else byStage.set(t.stage.id, [t]);
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id;
    if (typeof overId !== 'string' || !overId.startsWith(STAGE_PREFIX)) return;
    const stageId = Number(overId.slice(STAGE_PREFIX.length));
    const ticketNumber = Number(String(event.active.id).slice(TICKET_PREFIX.length));
    const ticket = items.find((t) => t.ticketNumber === ticketNumber);
    const stage = stages.find((s) => s.id === stageId);
    // spec 024: o espelhado não se move aqui (a API recusaria com 409)
    if (!ticket || ticket.mirrored || !stage || ticket.stage.id === stageId) return;
    move.mutate({ ticketNumber, stageId, stageName: stage.name });
  };

  if (stages.length === 0) return <EmptyData message="Esta mesa não possui estágios" />;

  // spec 011 → 024: numa mesa espelhada do TiFlux o ESPELHADO é só leitura
  // (mover card é PATCH de estágio, recusado com 409), mas o ticket fora do
  // espelho (cliente operado no ERP) se move como qualquer outro — quem decide
  // é o `mirrored` de cada card, que vem da API. Criar aqui só vale para
  // cliente operado no ERP; sem nenhum, o "+" some como antes.
  const deskMirrored = deskQ.data.origin === 'tiflux';
  const canCreate = !deskMirrored || operated.size > 0;

  return (
    <div className="flex flex-1 flex-col gap-2">
      {deskMirrored ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <MirroredBadge label="Mesa espelhada do TiFlux — edite no TiFlux" />
          <span>
            Os tickets desta mesa são atualizados a partir do TiFlux a cada sincronização
            {operated.size > 0 ? ' — menos os operados no ERP, que se editam aqui.' : '.'}
          </span>
        </div>
      ) : null}
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div className="flex flex-1 items-start gap-3 overflow-x-auto pb-2">
          {stages.map((stage) => (
            <BoardColumn
              key={stage.id}
              stage={stage}
              tickets={byStage.get(stage.id) ?? []}
              onNewTicket={canCreate ? onNewTicket : undefined}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
}

function BoardColumn({
  stage,
  tickets,
  onNewTicket,
}: {
  stage: DeskStage;
  tickets: TicketListItem[];
  onNewTicket?: ((stageId: number) => void) | undefined;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${STAGE_PREFIX}${stage.id}` });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex w-72 shrink-0 flex-col rounded-lg bg-muted p-2 transition-shadow',
        isOver && 'ring-2 ring-primary/40'
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1 py-0.5">
        <span className="truncate text-sm font-semibold text-foreground" title={stage.name}>
          {stage.name}
        </span>
        <CountPill count={tickets.length} />
        {onNewTicket ? (
          <button
            type="button"
            title="Novo ticket neste estágio"
            aria-label={`Novo ticket em ${stage.name}`}
            onClick={() => onNewTicket(stage.id)}
            className="ml-auto flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity hover:opacity-90"
          >
            <PlusIcon className="size-4" />
          </button>
        ) : null}
      </div>
      <div className="flex min-h-40 max-h-[calc(100dvh-300px)] flex-col gap-2 overflow-y-auto">
        {tickets.map((t) => (
          <BoardCard key={t.ticketNumber} ticket={t} draggable={!t.mirrored} />
        ))}
      </div>
    </div>
  );
}

function BoardCard({ ticket, draggable }: { ticket: TicketListItem; draggable: boolean }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${TICKET_PREFIX}${ticket.ticketNumber}`,
    disabled: !draggable,
  });
  // Suprime o clique disparado logo após o fim do drag (navegaria para fora).
  const wasDragged = useRef(false);
  useEffect(() => {
    if (isDragging) wasDragged.current = true;
  }, [isDragging]);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined}
      onClick={() => {
        if (wasDragged.current) {
          wasDragged.current = false;
          return;
        }
        void navigate({
          to: '/tickets/$ticketNumber',
          params: { ticketNumber: String(ticket.ticketNumber) },
        });
      }}
      className={cn(
        'rounded-md bg-card p-3 shadow-sm transition-shadow select-none hover:shadow-md',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        isDragging && 'relative z-50 opacity-70 shadow-lg'
      )}
    >
      <div className="text-xs font-semibold text-primary">#{ticket.ticketNumber}</div>
      <div
        className="mt-1 line-clamp-2 text-sm font-medium leading-snug text-foreground"
        title={ticket.title}
      >
        {ticket.title}
      </div>
      <div className="mt-1 truncate text-xs text-muted-foreground" title={ticket.client.name}>
        {ticket.client.name}
      </div>
      <div className="mt-2 flex min-h-6 items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1">
          {ticket.priority ? <PriorityChip name={ticket.priority.name} /> : null}
          {/* Chip de SLA só quando relevante (Vencido / A vencer). */}
          {(() => {
            const sla = listSlaState(ticket, new Date());
            return sla ? <SlaChip state={sla} /> : null;
          })()}
        </span>
        {ticket.responsible ? (
          <Avatar className="size-6" title={ticket.responsible.name}>
            <AvatarFallback className="bg-primary text-[10px] font-semibold text-primary-foreground">
              {initials(ticket.responsible.name)}
            </AvatarFallback>
          </Avatar>
        ) : null}
      </div>
    </div>
  );
}

function BoardSkeleton() {
  return (
    <div className="flex items-start gap-3 overflow-hidden pb-2" aria-busy="true">
      {[0, 1, 2, 3].map((c) => (
        <div key={c} className="flex w-72 shrink-0 flex-col gap-2 rounded-lg bg-muted p-2">
          <div className="flex items-center gap-2 px-1 py-0.5">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-5 w-6 rounded-full" />
          </div>
          {Array.from({ length: 3 - c % 2 }).map((_, r) => (
            <div key={r} className="rounded-md bg-card p-3 shadow-sm">
              <Skeleton className="h-3 w-14" />
              <Skeleton className="mt-2 h-4 w-full" />
              <Skeleton className="mt-1 h-4 w-2/3" />
              <div className="mt-3 flex items-center justify-between">
                <Skeleton className="h-[22px] w-14 rounded-[4px]" />
                <Skeleton className="size-6 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
